import React from "react";
import { useParams, useHistory } from "react-router-dom";
import useSWR from "swr";
import fetcher from "../services/fetcher";
import Header from "../components/Header";
import debounce from "../utils/debounce";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { DateUtils } from "react-day-picker";
import trackEvent from "../utils/trackevent";
import Footer from "../components/Footer";

export default function Contracts() {
  const { uuid } = useParams();
  const history = useHistory();

  const [recentHotelInfo, setRecentHotelInfo] = React.useState({});
  const [status, setStatus] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("check_out");
  const [sortDir, setSortDir] = React.useState("desc");
  const [searchTerm, setSearchTerm] = React.useState("");

  const composeUrl = () => {
    let url = `${process.env.REACT_APP_HE_API_HOST}/hotel_partners/${uuid}/contracts`;

    if (status !== "all") {
      url += `?state=${status}`;
    }

    if (searchTerm) {
      url += url.includes("?") ? "&" : "?";
      url += `search=${searchTerm}`;
      // url += `${status !== "all" ? "&" : "?"}search=${searchTerm}`;
    }

    if (sortKey) {
      url += url.includes("?") ? "&" : "?";
      url += `order_by=${sortKey}&order=${sortDir}`;
    }

    return url;
  };

  const { data = [], isValidating, error } = useSWR(composeUrl(), fetcher, {
    errorRetryCount: 1,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  // React.useEffect(() => {
  //   if (Object.keys(data).length) {
  //     setRecentData(data);
  //   }
  // }, [data]);

  const { data: hotelInfo = {} } = useSWR(
    `${process.env.REACT_APP_HE_API_HOST}/hotel_partners/${uuid}`,
    fetcher,
    {
      errorRetryCount: 1,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  React.useEffect(() => {
    if (Object.keys(hotelInfo).length) {
      setRecentHotelInfo(hotelInfo);
    }
  }, [hotelInfo]);

  const headers = [
    {
      key: "group_reservation_id",
      label: "Reservation #",
      formatDisplay: (data) => data,
    },
    {
      key: "guest_first",
      label: "First Name",
      formatDisplay: (data) => data,
    },
    {
      key: "guest_last",
      label: "Last Name",
      formatDisplay: (data) => data,
    },
    {
      key: "check_in",
      label: "Check In",
      formatDisplay: (data) => format(utcToZonedTime(data), "M/d/yy"),
    },
    {
      key: "check_out",
      label: "Check Out",
      formatDisplay: (data) => format(utcToZonedTime(data), "M/d/yy"),
    },
    {
      key: "state",
      label: "Status",
      formatDisplay: (data) => {
        if (data === "completed") {
          return "Checked Out";
        }

        if (data === "needs_attention") {
          return "Needs Attention";
        }

        return data;
      },
    },
    {
      key: "",
      label: "",
      formatDisplay: (data) => data,
    },
  ];

  const handleSearch = debounce((term) => {
    setSearchTerm(term);
    trackEvent("click", {
      eventCategory: "Rooming List - Search",
      eventLabel: term,
    });
  }, 500);

  const handleFilter = (e) => {
    const { value } = e.target;
    setStatus(value);
    trackEvent("click", {
      eventCategory: "Rooming List - Filter",
      eventLabel: value,
    });
  };

  const handleRowClick = (contract_number) => {
    history.push(`contracts/${contract_number}`);
  };

  const handleSort = (key) => {
    if (key === "state") return;
    if (sortKey === key) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
      trackEvent("click", {
        eventCategory: "Room List - Sort",
        eventLabel: `${key} - ${sortDir === "desc" ? "asc" : "desc"}`,
      });
    } else {
      setSortKey(key);
      setSortDir("desc");
      trackEvent("click", {
        eventCategory: "Rooming List - Sort",
        eventLabel: `${key} - desc`,
      });
    }
  };

  // const sort = (arr) => {
  //   return arr.sort((a, b) => {
  //     if (a[sortKey] < b[sortKey]) return sortDir === "desc" ? -1 : 1;
  //     if (a[sortKey] > b[sortKey]) return sortDir === "desc" ? 1 : -1;
  //     return 0;
  //   });
  // };

  const disable = (row) => {
    if (
      row.pending_update &&
      DateUtils.isDayBefore(new Date(row.check_out), new Date())
    ) {
      return false;
    } else if (row.pending_update) {
      return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col container mx-auto items-center mt-4">
      <Header {...recentHotelInfo} readOnly maxWidth="lg:max-w-4xl" />
      <div className="w-full max-w-2xl lg:max-w-4xl px-4 md:px-0">
        <div className="flex w-full mb-4 flex-wrap md:flex-no-wrap md:justify-between">
          <div className="relative w-full md:w-1/3">
            <input
              type="search"
              className="w-full pl-10 pr-4 py-2 rounded shadow focus:outline-none focus:shadow-outline text-gray-600 font-medium"
              placeholder="Search..."
              onChange={(e) => handleSearch(e.target.value)}
            />
            <div className="absolute top-0 left-0 inline-flex items-center p-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="p-1 text-gray-500">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>
          <div className="relative mt-2 w-full md:mt-0 md:w-1/5">
            <select
              className="block appearance-none w-full rounded shadow bg-white text-gray-700 font-medium pl-4 py-2 pr-8"
              name="status"
              id="status"
              value={status}
              onChange={handleFilter}>
              <option value="all">All</option>
              <option value="booked">Booked</option>
              <option value="visiting">Visiting</option>
              <option value="completed">Checked Out</option>
              <option value="needs_attention">Needs Attention</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ml-1">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
          </div>
        </div>
        <div className="w-full shadow bg-white relative overflow-auto rounded mb-4">
          <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
            <thead>
              <tr className="text-left">
                {headers.map((header) => {
                  return (
                    <th
                      key={header.key || "header-blank"}
                      title={
                        header.key !== "state" ? `Sort by ${header.label}` : ""
                      }
                      className="bg-blue-500 sticky top-0 border-b border-gray-200 pl-2 pr-0 first:pl-4 last:pr-2 py-2 text-white font-bold tracking-wider uppercase text-xs cursor-pointer"
                      style={header.key === "state" ? { minWidth: 107 } : {}}
                      onClick={() => handleSort(header.key)}>
                      <span className="inline-flex items-center">
                        {header.label}
                        {header.key !== "state" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`ml-1 ${
                              sortKey === header.key
                                ? "opacity-100"
                                : "opacity-0"
                            }`}>
                            {sortDir === "desc" ? (
                              <polyline points="6 9 12 15 18 9"></polyline>
                            ) : (
                              <polyline points="18 15 12 9 6 15"></polyline>
                            )}
                          </svg>
                        )}
                        {header.key === "state" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`ml-1 ${
                              status !== "all" ? "opacity-100" : "opacity-0"
                            }`}>
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                          </svg>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {error && !isValidating && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center p-10 text-red-500 uppercase font-bold">
                    <span className="flex w-full items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-4">
                        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"></polygon>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {error.message}
                    </span>
                    {status !== "all" && (
                      <button
                        className="ml-4 rounded border px-2 py-1"
                        onClick={() =>
                          handleFilter({ target: { value: "all" } })
                        }>
                        Clear Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {isValidating && (
                <tr>
                  <td colSpan={6} className="text-center p-10 text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="inline rotate-center">
                      <line x1="12" y1="2" x2="12" y2="6"></line>
                      <line x1="12" y1="18" x2="12" y2="22"></line>
                      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                      <line x1="2" y1="12" x2="6" y2="12"></line>
                      <line x1="18" y1="12" x2="22" y2="12"></line>
                      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                    </svg>
                  </td>
                </tr>
              )}
              {!error &&
                !isValidating &&
                !!data &&
                !!data.length &&
                data.map((row) => (
                  <tr
                    key={row.contract_number}
                    title={disable(row) ? "Pending Update" : "Click to Update"}
                    className={`${
                      disable(row) ? "" : "tr-hover cursor-pointer"
                    }`}
                    onClick={
                      disable(row)
                        ? () => {}
                        : () => handleRowClick(row.contract_number)
                    }>
                    {headers.map((head, i) => {
                      if (disable(row) && i === headers.length - 1) return null;
                      if (disable(row) && head.key === "state") {
                        return (
                          <td
                            key={`${row.contract_number}-${head.key}`}
                            colSpan={2}
                            className={`border-solid border-t border-gray-200 px-2 first:pl-4 py-2 ${
                              sortKey === head.key ? "bg-gray-100" : ""
                            } bg-gray-200 text-gray-500`}>
                            <span className="flex items-center capitalize relative">
                              Update Pending
                            </span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={`${row.contract_number}-${head.key}`}
                          className={`border-solid border-t border-gray-200 px-2 first:pl-4 py-2 last:px-0 ${
                            sortKey === head.key ? "bg-gray-100" : ""
                          } ${
                            disable(row)
                              ? "bg-gray-200 text-gray-500"
                              : "text-gray-700"
                          }`}>
                          <span className="flex items-center capitalize relative">
                            {row.pending_update && head.key === "state"
                              ? "Update Pending"
                              : head.formatDisplay(row[head.key])}
                            {i === headers.length - 1 && (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              {!error && !isValidating && data && !data.length && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center p-10 text-gray-500 uppercase">
                    <span>No Contracts</span>
                    {status !== "all" && (
                      <button
                        className="ml-4 rounded border px-2 py-1"
                        onClick={() =>
                          handleFilter({ target: { value: "all" } })
                        }>
                        Clear Filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Footer maxWidth="lg:max-w-4xl" />
    </div>
  );
}
